import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useTranslation } from '../../../i18n';
import { BRAND_COLORS } from '../../../styles/theme';
import type { AdminDialogProps, RecalculationResult } from '../types/adminTools.types';

interface MaintenanceRecalculateDialogProps extends AdminDialogProps {
  loading: boolean;
  recalculating: boolean;
  droneSerials: string[];
  selectedDrone: string;
  result: RecalculationResult | null;
  error: string;
  showDetails: boolean;
  onSelectDrone: (serial: string) => void;
  onRecalculate: () => Promise<void>;
  onToggleDetails: () => void;
}

export const MaintenanceRecalculateDialog: React.FC<MaintenanceRecalculateDialogProps> = ({
  open,
  onClose,
  loading,
  recalculating,
  droneSerials,
  selectedDrone,
  result,
  error,
  showDetails,
  onSelectDrone,
  onRecalculate,
  onToggleDetails,
}) => {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onClose={recalculating ? undefined : onClose}
      maxWidth="md"
      fullWidth
      data-testid="maintenance-recalculate-dialog"
    >
      <DialogTitle>{t('adminToolsPage.maintenanceRecalculate.title')}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* Drone selector */}
          <FormControl fullWidth disabled={loading || recalculating}>
            <InputLabel>{t('adminToolsPage.maintenanceRecalculate.selectDrone')}</InputLabel>
            <Select
              value={selectedDrone}
              label={t('adminToolsPage.maintenanceRecalculate.selectDrone')}
              onChange={(e) => onSelectDrone(e.target.value)}
            >
              {loading ? (
                <MenuItem disabled>
                  <CircularProgress size={16} sx={{ mr: 1 }} /> {t('common.loading')}
                </MenuItem>
              ) : (
                droneSerials.map((serial) => (
                  <MenuItem key={serial} value={serial}>
                    {serial}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>

          {/* Info text */}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('adminToolsPage.maintenanceRecalculate.infoText')}
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>
              <Typography variant="body2" color="text.secondary">
                {t('adminToolsPage.maintenanceRecalculate.infoBullet1')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                {t('adminToolsPage.maintenanceRecalculate.infoBullet2')}
              </Typography>
            </li>
            <li>
              <Typography variant="body2" color="text.secondary">
                {t('adminToolsPage.maintenanceRecalculate.infoBullet3')}
              </Typography>
            </li>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}

          {/* Results panel */}
          {result && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: '#f5f9f5',
                borderRadius: 1,
                border: '1px solid #c8e6c9',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 1,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2e7d32' }}>
                    {t('adminToolsPage.maintenanceRecalculate.resultComplete')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t('adminToolsPage.maintenanceRecalculate.resultSummary', {
                      processed: String(result.summary.maintenance_records_processed),
                      reset: String(result.summary.counters_reset),
                      unchanged: String(result.summary.counters_unchanged),
                    })}
                  </Typography>
                </Box>
                <Button
                  size="small"
                  endIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  onClick={onToggleDetails}
                  sx={{ textTransform: 'none' }}
                >
                  {showDetails
                    ? t('adminToolsPage.maintenanceRecalculate.hideDetails')
                    : t('adminToolsPage.maintenanceRecalculate.viewDetails')}
                </Button>
              </Box>

              <Collapse in={showDetails}>
                <Box sx={{ mt: 2 }}>
                  {/* Summary Stats */}
                  <Box
                    sx={{
                      display: 'flex',
                      gap: 3,
                      mb: 2,
                      p: 2,
                      bgcolor: 'white',
                      borderRadius: 1,
                    }}
                  >
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('adminToolsPage.maintenanceRecalculate.totalFlightHours')}
                      </Typography>
                      <Typography variant="h6">
                        {result.summary.total_flight_hours.toFixed(1)}h
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        {t('adminToolsPage.maintenanceRecalculate.totalEngineHours')}
                      </Typography>
                      <Typography variant="h6">
                        {result.summary.total_engine_hours.toFixed(1)}h
                      </Typography>
                    </Box>
                  </Box>

                  {/* Counters Breakdown Table */}
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                    {t('adminToolsPage.maintenanceRecalculate.countersBreakdown')}
                  </Typography>
                  <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            {t('adminToolsPage.maintenanceRecalculate.tableCounter')}
                          </TableCell>
                          <TableCell>
                            {t('adminToolsPage.maintenanceRecalculate.tableLastMaintenance')}
                          </TableCell>
                          <TableCell>
                            {t('adminToolsPage.maintenanceRecalculate.tableFlightsSince')}
                          </TableCell>
                          <TableCell align="right">
                            {t('adminToolsPage.maintenanceRecalculate.tableTotalHours')}
                          </TableCell>
                          <TableCell align="right">
                            {t('adminToolsPage.maintenanceRecalculate.tableRemaining')}
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {[...result.counters_reset, ...result.counters_unchanged].map((counter) => (
                          <TableRow
                            key={counter.counter_key}
                            sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {counter.counter_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {counter.hour_type === 'flight_hours'
                                  ? t('adminToolsPage.maintenanceRecalculate.flightHoursLabel')
                                  : t('adminToolsPage.maintenanceRecalculate.engineHoursLabel')}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {counter.reset_by ? (
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                    {counter.reset_by.date.split('T')[0]}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    color="text.secondary"
                                  >
                                    {t('adminToolsPage.maintenanceRecalculate.byPerformer', {
                                      name: counter.reset_by.performed_by,
                                    })}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  {t('adminToolsPage.maintenanceRecalculate.neverMaintained')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {counter.flights_since_maintenance &&
                              counter.flights_since_maintenance.length > 0 ? (
                                <Box sx={{ maxHeight: 100, overflowY: 'auto' }}>
                                  {counter.flights_since_maintenance.map((flight, idx: number) => {
                                    const shortDate = flight.date.split('T')[0];
                                    // NULL = not extracted (#1164), shown as N/A, never 0.
                                    const raw =
                                      counter.hour_type === 'flight_hours'
                                        ? flight.flight_hours
                                        : flight.engine_hours;
                                    const hours =
                                      raw == null ? t('common.notAvailable') : `${raw.toFixed(1)}h`;
                                    return (
                                      <Typography
                                        key={idx}
                                        variant="caption"
                                        display="block"
                                        sx={{ lineHeight: 1.6 }}
                                      >
                                        {shortDate}: {hours}
                                      </Typography>
                                    );
                                  })}
                                </Box>
                              ) : (
                                <Typography variant="caption" color="text.secondary">
                                  {t('adminToolsPage.maintenanceRecalculate.noFlights')}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Chip
                                label={`${counter.new_value.toFixed(1)}h`}
                                size="small"
                                color={counter.changed ? 'success' : 'default'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography
                                variant="body2"
                                sx={{
                                  color: counter.status === 'overdue' ? '#d32f2f' : '#2e7d32',
                                  fontWeight: counter.status === 'overdue' ? 600 : 400,
                                }}
                              >
                                {counter.hours_remaining !== null
                                  ? `${counter.hours_remaining.toFixed(1)}h`
                                  : '-'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Collapse>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={recalculating}>
          {t('adminToolsPage.close')}
        </Button>
        <Button
          onClick={onRecalculate}
          variant="contained"
          disabled={!selectedDrone || recalculating}
          startIcon={recalculating ? <CircularProgress size={16} /> : <RefreshIcon />}
          sx={{
            backgroundColor: BRAND_COLORS.forestGreen.main,
            '&:hover': {
              backgroundColor: BRAND_COLORS.forestGreen.main,
              opacity: 0.8,
            },
          }}
        >
          {recalculating
            ? t('adminToolsPage.maintenanceRecalculate.recalculating')
            : t('adminToolsPage.maintenanceRecalculate.recalculate')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
